import { LayoutStyle } from '~/core/layout-style';
import { GoogleAuthProvider } from 'firebase/auth';
import { StripeCheckoutDisplayMode } from '~/lib/stripe/types';
import pricingData from '~/lib/stripe/pricing.json';

enum Themes {
  Light = 'light',
  Dark = 'dark',
}

type StripeMode = keyof typeof pricingData.stripeAccounts;

const STRIPE_PUBLISHABLE_KEY = process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY;

function getStripeMode(): StripeMode {
  if (process.env.NEXT_PUBLIC_ENVIRONMENT === 'production') {
    return 'live';
  }

  if (
    STRIPE_PUBLISHABLE_KEY === pricingData.stripeAccounts.live.publishableKey
  ) {
    return 'live';
  }

  return 'test';
}

const STRIPE_MODE = getStripeMode();

/**
 * Build Stripe products from pricing.json
 * Public Stripe identifiers come from the selected catalog account.
 */
function buildStripeProducts() {
  return pricingData.subscriptions.map((sub) => {
    const priceQualifier =
      'priceQualifier' in sub && typeof sub.priceQualifier === 'string'
        ? sub.priceQualifier
        : undefined;

    return {
      tier: sub.tier,
      name: sub.name,
      availableForPurchase: sub.availableForPurchase,
      description: sub.description,
      badge: sub.badge,
      recommended: sub.recommended ?? false,
      features: sub.features,
      plans: [
        {
          name: 'Monthly',
          price: sub.price.display,
          unitAmount: sub.price.amount,
          priceQualifier,
          stripePriceId: sub.stripePriceIds[STRIPE_MODE],
        },
      ],
    };
  });
}

/**
 * Build credit packages from pricing.json
 * Public Stripe identifiers come from the selected catalog account.
 */
function buildCreditPackages() {
  return pricingData.creditPacks.map((pack) => {
    return {
      id: pack.id,
      name: pack.name,
      description: pack.description,
      price: pack.price.display,
      credits: pack.credits,
      stripePriceId: pack.stripePriceIds[STRIPE_MODE],
    };
  });
}

const configuration = {
  site: {
    name: 'DirectorDeck - Cinematic Presentations',
    description: 'One click transforms your slides to cinematic splendor.',
    themeColor: '#524cc8',
    themeColorDark: '#0a0a0f',
    siteUrl: process.env.NEXT_PUBLIC_SITE_URL as string,
    siteName: 'DirectorDeck',
    twitterHandle: '',
    githubHandle: '',
    convertKitFormId: '',
    locale: process.env.DEFAULT_LOCALE,
  },
  cloudRun: {
    directUrl: process.env.NEXT_PUBLIC_CLOUD_RUN_URL?.trim(),
  },
  firebase: {
    apiKey: process.env.NEXT_PUBLIC_FIREBASE_API_KEY,
    authDomain: process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID,
    storageBucket: process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.NEXT_PUBLIC_FIREBASE_APP_ID,
  },
  auth: {
    // Enable MFA. You must upgrade to GCP Identity Platform to use it.
    // see: https://cloud.google.com/identity-platform/docs/product-comparison
    enableMultiFactorAuth: true,
    // When enabled, users will be required to verify their email address
    // before being able to access the app
    requireEmailVerification:
      process.env.NEXT_PUBLIC_REQUIRE_EMAIL_VERIFICATION === 'true',
    // NB: Enable the providers below in the Firebase Console
    // in your production project
    providers: {
      emailPassword: true,
      phoneNumber: false,
      emailLink: false,
      oAuth: [GoogleAuthProvider],
    },
    // Comma-separated list of test emails that can bypass verification
    verificationBypassEmails: getVerificationBypassEmails(),
    // Use Redirect or Popup strategy for oAuth.
    // Popup is more reliable when app domain differs from Firebase authDomain
    useRedirectStrategy: false,
  },
  environment: process.env.NODE_ENV ?? 'development',
  emulatorHost:
    process.env.NEXT_PUBLIC_FIREBASE_EMULATOR_HOST ??
    process.env.NEXT_PUBLIC_EMULATOR_HOST,
  storageEmulatorPort: process.env.NEXT_PUBLIC_FIREBASE_STORAGE_EMULATOR_PORT,
  emulator: process.env.NEXT_PUBLIC_EMULATOR === 'true',
  production: process.env.NODE_ENV === 'production',
  theme: Themes.Dark,
  features: {
    enableThemeSwitcher: true,
    enableAccountDeletion: getBoolean(
      process.env.NEXT_PUBLIC_ENABLE_ACCOUNT_DELETION,
      false,
    ),
    enableOrganizationDeletion: getBoolean(
      process.env.NEXT_PUBLIC_ENABLE_ORGANIZATION_DELETION,
      false,
    ),
    directCloudRunRequests: process.env.NEXT_PUBLIC_DIRECT_CLOUD_RUN === 'true',
    directCloudRunDesignRequests:
      process.env.NEXT_PUBLIC_DIRECT_CLOUD_RUN_DESIGN === 'true',
  },
  paths: {
    signIn: '/auth/sign-in',
    signUp: '/auth/sign-up',
    emailLinkSignIn: '/auth/link',
    onboarding: `/onboarding`,
    appHome: '/presentations',
    settings: {
      profile: '/settings/profile',
      organization: '/settings/organization',
      subscription: '/settings/subscription',
      authentication: '/settings/profile/authentication',
      email: '/settings/profile/email',
      password: '/settings/profile/password',
    },
    api: {
      checkout: `/api/stripe/checkout`,
      billingPortal: `/api/stripe/portal`,
    },
  },
  navigation: {
    style: LayoutStyle.Sidebar,
  },
  appCheckSiteKey: process.env.NEXT_PUBLIC_APPCHECK_KEY,
  sentry: {
    serverEnabled:
      getBoolean(process.env.SENTRY_ENABLED, false) &&
      getBoolean(process.env.NEXT_PUBLIC_SENTRY_ENABLED, false) &&
      process.env.NEXT_PUBLIC_ENVIRONMENT === 'production',
    clientEnabled:
      getBoolean(process.env.NEXT_PUBLIC_SENTRY_ENABLED, false) &&
      process.env.NEXT_PUBLIC_ENVIRONMENT === 'production',
    environment: process.env.NEXT_PUBLIC_ENVIRONMENT ?? process.env.NODE_ENV,
    release:
      process.env.SENTRY_RELEASE ??
      process.env.BUILD_ID ??
      process.env.NEXT_BUILD_ID ??
      'dev',
    serverDsn: process.env.SENTRY_DSN,
    clientDsn: process.env.NEXT_PUBLIC_SENTRY_DSN ?? process.env.SENTRY_DSN,
  },
  stripe: {
    enabled: getBoolean(process.env.NEXT_PUBLIC_STRIPE_ENABLED, true),
    embedded:
      getBoolean(process.env.NEXT_PUBLIC_STRIPE_ENABLED, true) &&
      getBoolean(process.env.NEXT_PUBLIC_STRIPE_EMBEDDED, true),
    displayMode: StripeCheckoutDisplayMode.Popup,
    mode: STRIPE_MODE,
    serverKeyPrefixes: {
      test: pricingData.stripeAccounts.test.serverKeyPrefix,
      live: pricingData.stripeAccounts.live.serverKeyPrefix,
    },
    publishableKey:
      STRIPE_PUBLISHABLE_KEY ??
      pricingData.stripeAccounts[STRIPE_MODE].publishableKey,
    products: buildStripeProducts(),
    creditPackages: buildCreditPackages(),
  },
};

export default configuration;

// Validate Stripe configuration
// as this is a new requirement, we throw an error if the key is not defined
// in the environment
if (
  configuration.stripe.enabled &&
  configuration.production &&
  !STRIPE_PUBLISHABLE_KEY
) {
  throw new Error(
    'NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY is not defined. Please' +
      ' add it to your environment variables.',
  );
}

if (
  configuration.stripe.enabled &&
  configuration.production &&
  STRIPE_PUBLISHABLE_KEY !==
    pricingData.stripeAccounts[configuration.stripe.mode].publishableKey
) {
  throw new Error(
    'NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY does not match the configured Stripe catalog.',
  );
}

function getBoolean(value: unknown, defaultValue: boolean) {
  if (typeof value === 'string') {
    return value === 'true';
  }

  return defaultValue;
}

function getVerificationBypassEmails() {
  const emails = parseEmailList(
    process.env.NEXT_PUBLIC_EMAIL_VERIFICATION_BYPASS_EMAILS,
  );

  if (process.env.NEXT_PUBLIC_ENVIRONMENT === 'staging') {
    emails.add('test-e2e@directordeck.com');
  }

  return Array.from(emails);
}

function parseEmailList(value: string | undefined): Set<string> {
  const emails = (value ?? '')
    .split(/[\s,;|]+/)
    .map((email) => email.trim().toLowerCase())
    .filter(Boolean);

  return new Set(emails);
}
